import { useEffect, useState, type ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ListPagination } from "@/components/ui/list-pagination";
import { cn } from "@/components/utils/utils";

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  headClassName?: string;
  cellClassName?: string;
}

/** Paginazione lato server: `data` e' gia' la pagina corrente, il totale lo da' il chiamante. */
export interface DataTableServerPagination {
  /** Pagina corrente, da 1 */
  page: number;
  /** Righe di tutte le pagine */
  totalItems: number;
  onPageChange: (page: number) => void;
  /** Blocca i controlli mentre arriva la pagina */
  disabled?: boolean;
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedRowId?: string | null;
  /** Classi extra per riga (es. sfondo delle righe in ritardo). */
  getRowClassName?: (row: T) => string | undefined;
  /** Reso in una colonna azioni in coda; il wrapper cella ferma la propagazione del click. Se assente, nessuna colonna azioni. */
  renderActions?: (row: T) => ReactNode;
  /** Header della colonna azioni (es. t('common.actions')). */
  actionsHeader?: ReactNode;
  /** Classi extra sull'header della colonna azioni (es. `w-[56px]` per larghezza fissa). */
  actionsHeadClassName?: string;
  /** Righe per pagina (default 10). Ignorato quando `paginated` è false. */
  pageSize?: number;
  /**
   * Paginazione client-side (default true, comportamento storico). Con `false`
   * il footer sparisce e vengono renderizzate TUTTE le righe: serve alle liste
   * "scroll infinito" già interamente in memoria (es. admin email templates),
   * dove spezzare in pagine è solo attrito.
   */
  paginated?: boolean;
  /**
   * Modalita' controllata: niente slicing interno, la pagina e il totale li da' il
   * chiamante (RPC paginate). Con questa prop `paginated` e' ignorato.
   */
  serverPagination?: DataTableServerPagination;
  isLoading?: boolean;
  /**
   * Cambiandolo, le righe si rimontano e rifanno l'animazione di entrata anche se gli id
   * sono gli stessi (es. dopo "Aggiorna"). Tipicamente la `version` del caricamento.
   */
  animationKey?: string | number;
  /** Nodo mostrato quando data è vuoto (non in loading). */
  emptyState?: ReactNode;
  /** classi extra sul contenitore radice. */
  className?: string;
  /**
   * Classi extra sull'elemento `<table>`. Utile per imporre una larghezza minima
   * (es. `min-w-[1100px]`) e ottenere lo scroll orizzontale su mobile senza
   * rinunciare al full-width su desktop.
   */
  tableClassName?: string;
}

/**
 * Tabella UI generica e dominio-agnostica.
 *
 * - Occupa tutta l'altezza del parent (`h-full`) e scrolla SOLO le righe:
 *   header sticky in cima all'area scroll, footer paginazione fisso e non scrollabile.
 * - Paginazione client-side (uncontrolled): i dati sono già tutti in memoria e
 *   vengono sliceati per pagina. Disattivabile con `paginated={false}` (footer
 *   nascosto, tutte le righe renderizzate nell'area di scroll).
 * - Paginazione server-side con `serverPagination`: `data` e' gia' la pagina,
 *   pagina e totale li gestisce il chiamante.
 * - Footer: ListPagination, lo stesso delle viste a griglia.
 */
export function DataTable<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  selectedRowId,
  getRowClassName,
  renderActions,
  actionsHeader,
  actionsHeadClassName,
  pageSize = 12,
  paginated = true,
  serverPagination,
  isLoading,
  animationKey,
  emptyState,
  className,
  tableClassName,
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);

  const clientPaginated = paginated && !serverPagination;
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pageRows = clientPaginated
    ? data.slice(startIndex, startIndex + pageSize)
    : data;

  // Clamp: quando `data` si riduce (es. filtri applicati dal parent) il numero
  // di pagine può calare e `currentPage` restare fuori range, mostrando una
  // pagina vuota. Dipendiamo da `totalPages` (non da `data`) per non resettare
  // ad ogni render.
  useEffect(() => {
    if (!clientPaginated) return;
    if (currentPage > totalPages) setCurrentPage(1);
  }, [clientPaginated, totalPages, currentPage]);

  const columnCount = columns.length + (renderActions ? 1 : 0);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {/* Unico scroll-container: header sticky funziona solo se non c'è il
          wrapper overflow del componente Table di shadcn, per questo renderizziamo
          la <table> grezza usando solo le sotto-primitive. */}
      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className={cn("w-full caption-bottom text-sm table-fixed", tableClassName)}>
          <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 [&_tr]:border-b [&_tr]:border-gray-200">
            <TableRow className="hover:bg-transparent">
              {columns.map((c) => (
                <TableHead
                  key={c.id}
                  className={cn(
                    "h-11 text-xs font-semibold uppercase tracking-wide text-gray-500",
                    c.headClassName
                  )}
                >
                  {c.header}
                </TableHead>
              ))}
              {renderActions && (
                <TableHead
                  className={cn(
                    "h-11 text-right text-xs font-semibold uppercase tracking-wide text-gray-500",
                    actionsHeadClassName
                  )}
                >
                  {actionsHeader}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Righe scheletro con le colonne vere: la tabella ha gia' la sua forma
              // mentre i dati arrivano, invece di uno spinner che poi si allarga.
              Array.from({ length: Math.min(pageSize, 8) }, (_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} aria-hidden="true" className="border-b border-gray-100 hover:bg-transparent">
                  {columns.map((c, colIndex) => (
                    <TableCell key={c.id} className={c.cellClassName}>
                      <Skeleton className={colIndex === 0 ? "h-4 w-3/4" : "h-4 w-1/2"} />
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell className="text-right">
                      <Skeleton className="h-8 w-8 ml-auto" />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columnCount}
                  className="text-center text-sm text-muted-foreground py-16"
                >
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row, index) => (
                <TableRow
                  key={animationKey == null ? getRowId(row) : `${animationKey}-${getRowId(row)}`}
                  // Entrata leggera, sfalsata riga per riga come le card della griglia:
                  // le righe compaiono al posto dello scheletro invece di apparire di colpo.
                  style={{ animationDelay: `${Math.min(index, 12) * 30}ms`, animationFillMode: "backwards" }}
                  className={cn(
                    "border-b border-gray-100 transition-colors last:border-0",
                    "animate-in fade-in slide-in-from-bottom-1 duration-300",
                    onRowClick && "cursor-pointer",
                    "hover:bg-gray-50",
                    getRowClassName?.(row),
                    selectedRowId === getRowId(row) &&
                      "bg-blue-50 hover:bg-blue-100"
                  )}
                  onClick={() => onRowClick?.(row)}
                  // Riga cliccabile = raggiungibile anche da tastiera. Solo i tasti premuti
                  // sulla riga stessa: Invio su un bottone della colonna azioni e' suo.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((c) => (
                    <TableCell key={c.id} className={c.cellClassName}>
                      {c.cell(row)}
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderActions(row)}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </table>
      </div>

      {/* Footer paginazione: fuori dallo scroll, non scrollabile. */}
      {serverPagination && (
        <ListPagination
          // Scheletro solo finche' il totale non e' noto (primo caricamento): cambiando
          // pagina il paginatore resta li', disattivato, mentre le righe ricaricano.
          loading={isLoading && serverPagination.totalItems <= 0}
          page={serverPagination.page}
          pageSize={pageSize}
          totalCount={serverPagination.totalItems}
          onPageChange={serverPagination.onPageChange}
          disabled={serverPagination.disabled}
        />
      )}
      {clientPaginated && (
        <ListPagination
          loading={isLoading}
          page={currentPage}
          pageSize={pageSize}
          totalCount={data.length}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
}
